import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoneIcon from '@mui/icons-material/Done';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { Divider, Step, StepLabel, Stepper, useTheme } from '@mui/material';
import {
  BaseSidesheet,
  ButtonPrimary,
  CloseIcon,
  Spinner,
} from 'app/components';
import { stripTaxpayerId } from 'app/helpers';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { usePersonContext } from 'modules/customer/context';
import {
  EMaritalPropertySystem,
  EMaritalStatus,
  ESex,
} from 'modules/customer/context/PersonProvider/person.interfaces';
import { EPersonType } from 'modules/customer/context/PersonProvider/PersonProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AboutContainer from './About/About';
import AddressStep from './Address/Address';
import IdentificationStep from './Identification/Identification';
import RepresentativesContainer from './Representatives/Representatives';
import UploadDocuments from './UploadDocuments/UploadDocuments';
import UploadBusinessDocument from './UploadDocuments/UploadBusinessDocuments';
import {
  CloseIconWrapper,
  ModalFooter,
  SideSheetHeader,
  StyledButtonWrapper,
  StyledContentWrapper,
  StyledFooter,
  StyledHeader,
  StyledSteps,
} from './styles';
import { IRelativePayload } from 'modules/customer/services/api/interfaces';
import BankStep from './Bank/Bank';
import { IUpdateBusinessData } from 'modules/customer/services/api/useCustomerService';
import { serializePhoneObject } from 'app/utils/normalizer';
import { isValidPersonFullName } from 'modules/customer/helpers/personValidation';
import { EEducationLevel } from './types';

interface ICreatePersonModalProps {
  handleClose: () => void;
  isOpen: boolean;
  title?: string;
  hasRole?: boolean;
  successMessage?: string;
  formTypeRender?: EPersonType;
  hasBankInformation?: boolean;
}

const CreatePersonModal = ({
  handleClose,
  hasRole,
  isOpen,
  title = 'Cadastro',
  successMessage = 'Cadastro efetuado com sucesso!',
  formTypeRender,
  hasBankInformation,
}: ICreatePersonModalProps) => {
  /* Hooks */
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const {
    clearContext,
    createCompany,
    createdBusinessId,
    createdPersonId,
    createPerson,
    createRepresentative,
    formType,
    isLoading,
    person,
    personCompany,
    representatives,
    setCreatedBusinessId,
    setPerson,
    setPersonCompany,
    setRepresentatives,
    updateCompany,
  } = usePersonContext();

  /* States */
  const [activeStep, setActiveStep] = useState(0);
  const isPerson =
    formTypeRender === EPersonType.PERSON ||
    formType?.value === EPersonType.PERSON;

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    return;
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const closeModalHandler = () => {
    clearContext();
    setRepresentatives([]);
    handleClose();
  };

  const createOrUpdatePersonHandler = useCallback(async () => {
    // Create person
    if (isPerson) {
      const data = person;
      const response = await createPerson({
        birth_date: data?.birth_date,
        email_address: data?.email_address,
        full_name: data?.full_name?.trim() || '',
        nationality: data?.nationality?.trim() || '',
        occupation: data?.occupation?.trim() || '',
        education_level: data.education_level || EEducationLevel.NOT_INFORMED,
        pep:
          data?.pep && data.pep?.value ? data.pep.value === 'true' : undefined,
        marital_status:
          (data?.marital_status?.value as EMaritalStatus) || undefined,
        spouse_full_name: data.spouse_full_name ?? undefined,
        spouse_taxpayer_id:
          data.spouse_taxpayer_id?.replaceAll('.', '').replaceAll('-', '') ??
          undefined,
        spouse_date_of_birth: data.spouse_date_of_birth ?? undefined,
        marital_property_system:
          (data.marital_property_system?.value as EMaritalPropertySystem) ??
          undefined,
        mothers_name: data?.mothers_name
          ? data?.mothers_name?.trim()
          : undefined,
        sex: data?.sex?.value as ESex,
        phone: serializePhoneObject(data?.phone),
        taxpayer_id: data?.taxpayer_id ? stripTaxpayerId(data.taxpayer_id) : '',
        address: {
          city: data?.address?.city?.value.trim() || 'não informado',
          state_code: data?.address?.state_code?.value || '',
          country_code: '+55',
          district: data?.address?.district?.trim() || 'não informado',
          extra_info: data?.address?.extra_info?.trim() || '',
          postal_code: data?.address?.postal_code?.replace(/\D+/g, '') || '',
          street_name: data?.address?.street_name?.trim() || 'não informado',
          street_number: data?.address?.street_number?.trim() || '0',
        },
        monthly_income: parseFloat(data?.monthly_income || '0'),
        birthplace: data?.birthplace,
        ...(data?.external_bank_account?.bank_account &&
          data?.external_bank_account?.bank_account?.length > 0 && {
            external_bank_account: {
              ...data?.external_bank_account,
            },
          }),
        ...(data?.pix?.key &&
          data?.pix?.key?.length > 0 && {
            pix: {
              ...data?.pix,
            },
          }),
      });

      if (response) {
        showSnackbar(successMessage);
        handleNext();
        return;
      }

      return showSnackbar('Houve um erro ao registrar o cadastro.', 'error');
    }

    // Update company
    if (personCompany.id) {
      const parsedAddress = {
        city:
          personCompany?.address?.city?.value ||
          personCompany?.address?.cityManually ||
          '',
        country_code: 'BRA',
        district: personCompany?.address?.district || '',
        extra_info: personCompany?.address?.extra_info || '',
        postal_code: personCompany?.address?.postal_code || '',
        state_code: personCompany?.address?.state_code?.value || '',
        street_name: personCompany?.address?.street_name || '',
        street_number: personCompany?.address?.street_number || '',
      };

      const payload: IUpdateBusinessData = {
        ...personCompany,
        address: parsedAddress,
        billing_address: parsedAddress,
        incorporation_type: personCompany.incorporation_type?.value,
        monthly_revenue: parseFloat(personCompany.monthly_revenue || '0'),
        phone: serializePhoneObject(personCompany.phone),
        share_capital: parseFloat(personCompany.share_capital || '0'),
        tax_regime: personCompany.tax_regime?.value || '',
      };

      const res = await updateCompany(personCompany.id, payload);

      if (res) {
        const parsedRepresentatives: IRelativePayload[] = representatives.map(
          (r) => {
            const rawValue = r?.share || 0;
            const numericValue =
              Number(rawValue.toString().replace(/\D/g, '')) / 100;

            return {
              person: {
                id: r.id || '',
              },
              signer: r.signer?.label === 'Sim' ? true : false,
              type: r.workRelation?.value || '',
              share: numericValue ?? 0,
            };
          },
        );

        const resRepresentative = await createRepresentative(
          personCompany.id,
          parsedRepresentatives,
        );

        if (resRepresentative) {
          showSnackbar('Cadastro editado com sucesso!', 'success');
          setCreatedBusinessId(personCompany?.id);
          handleNext();
          return;
        }
      }

      return showSnackbar('Houve um erro ao atualizar o cadastro.', 'error');
    }

    // Create company
    const data = personCompany;
    const response = await createCompany({
      address: {
        city: data?.address?.city?.value.trim() || '',
        state_code: data?.address?.state_code?.value || '',
        country_code: '+55',
        district: data?.address?.district?.trim() || 'não informado',
        extra_info: data?.address?.extra_info?.trim() || '',
        postal_code: data?.address?.postal_code?.replace(/\D+/g, '') || '',
        street_name: data?.address?.street_name?.trim() || 'não informado',
        street_number: data?.address?.street_number?.trim() || '0',
      },
      foundation_date: data?.foundation_date,
      email_address: data?.email_address?.trim(),
      legal_name: data?.legal_name?.trim(),
      name: data?.name ? data?.name?.trim() : undefined,
      phone: {
        area_code: data?.phone.replace(/\D+/g, '').slice(0, 2),
        country_code: '55',
        number: data?.phone?.replace(/\D+/g, '').slice(2),
      },
      taxpayer_id: stripTaxpayerId(data?.taxpayer_id ?? ''),
      tax_regime: data?.tax_regime?.value || 'SIMPLE',
      qualification_request: data?.hasQualificationRequest
        ? {
            product: {
              id: data?.productId as string,
            },
            funding: {
              id: data?.fundingId as string,
            },
            role: data?.role,
          }
        : undefined,
      share_capital: parseFloat(data?.share_capital || '0'),
      monthly_revenue: parseFloat(data?.monthly_revenue || '0'),
      billing_address: {
        city: data?.address?.city?.value.trim() || '',
        state_code: data?.address?.state_code?.value || '',
        country_code: '+55',
        district: data?.address?.district?.trim() || 'não informado',
        extra_info: data?.address?.extra_info?.trim() || '',
        postal_code: data?.address?.postal_code?.replace(/\D+/g, '') || '',
        street_name: data?.address?.street_name?.trim() || 'não informado',
        street_number: data?.address?.street_number?.trim() || '0',
      },
      incorporation_type:
        data?.incorporation_type?.value &&
        data?.incorporation_type?.value.length > 0
          ? data?.incorporation_type?.value
          : undefined,
      industry_classification: data?.industry_classification || undefined,
    });

    if (response?.id) {
      setCreatedBusinessId(response?.id);

      const parsedRepresentatives: IRelativePayload[] = representatives.map(
        (r) => {
          const rawValue = r?.share || 0;
          const numericValue =
            Number(rawValue.toString().replace(/\D/g, '')) / 100;

          return {
            person: {
              id: r.id || '',
            },
            signer: r.signer?.label === 'Sim' ? true : false,
            type: r.workRelation?.value || '',
            share: numericValue ?? 0,
          };
        },
      );

      const res = await createRepresentative(
        response?.id,
        parsedRepresentatives,
      );

      if (res) {
        showSnackbar(successMessage);
        handleNext();
        return;
      }

      return showSnackbar('Houve um erro ao registrar o cadastro.', 'error');
    }
  }, [person, isPerson, personCompany, createdPersonId]);

  const steps = useMemo(() => {
    const baseSteps = [
      {
        label: 'Sobre',
        render: (
          <AboutContainer hasRole={hasRole} formTypeRender={formTypeRender} />
        ),
        actionButton: 'Avançar',
        icon: <ArrowForwardIosIcon fontSize="small" />,
        nextStep: handleNext,
        isButtonDisabled: isPerson
          ? !person?.taxpayer_id ||
            !isValidPersonFullName(person?.full_name) ||
            !person?.nationality ||
            !person?.occupation ||
            !person?.birth_date ||
            !person?.phone ||
            !person?.email_address
          : !personCompany?.taxpayer_id ||
            !personCompany?.legal_name ||
            !personCompany?.phone ||
            !personCompany?.email_address ||
            !personCompany?.foundation_date,
      },
      isPerson
        ? {
            label: 'Identificação',
            actionButton: 'Avançar',
            nextStep: handleNext,
            icon: <ArrowForwardIosIcon fontSize="small" />,
            render: (
              <IdentificationStep
                identification={person.identification}
                setIdentification={(identification) =>
                  setPerson({ ...person, identification })
                }
              />
            ),
          }
        : {
            label: 'Representantes',
            render: <RepresentativesContainer />,
            actionButton: 'Avançar',
            nextStep: handleNext,
            icon: <ArrowForwardIosIcon fontSize="small" />,
          },

      ...(hasBankInformation
        ? [
            {
              label: 'Dados Bancários',
              actionButton: 'Avançar',
              nextStep: handleNext,
              icon: <ArrowForwardIosIcon fontSize="small" />,
              render: (
                <BankStep
                  setBankInfo={(bankInfo) => {
                    const payload = {
                      external_bank_account: {
                        bank_account:
                          bankInfo.external_bank_account?.bank_account || '',
                        bank_account_digit:
                          bankInfo.external_bank_account?.bank_account_digit ||
                          '',
                        bank_account_type:
                          bankInfo.external_bank_account?.bank_account_type ||
                          '',
                        bank_branch:
                          bankInfo.external_bank_account?.bank_branch || '',
                        ispb_code:
                          bankInfo.external_bank_account?.ispb_code || '',
                        bank_code: bankInfo.external_bank_account?.bank_code,
                      },
                      pix: bankInfo.pix,
                    };

                    return isPerson
                      ? setPerson({
                          ...person,
                          ...payload,
                        })
                      : setPersonCompany({
                          ...personCompany,
                          ...payload,
                        });
                  }}
                  bankInfo={{
                    external_bank_account: isPerson
                      ? person.external_bank_account
                      : personCompany.external_bank_account,
                    pix: isPerson ? person.pix : personCompany.pix,
                  }}
                />
              ),
            },
          ]
        : []),

      {
        label: 'Endereço',
        actionButton: 'Cadastrar',
        nextStep: createOrUpdatePersonHandler,
        icon: <PersonAddAltIcon fontSize="small" />,
        render: (
          <AddressStep
            address={!isPerson ? personCompany.address : person.address}
            setAddress={(address) =>
              !isPerson
                ? setPersonCompany({ ...personCompany, address })
                : setPerson({ ...person, address })
            }
          />
        ),
        isButtonDisabled: isPerson
          ? !person.address?.postal_code ||
            !person.address.city ||
            !person.address?.state_code
          : !personCompany.address?.postal_code ||
            !personCompany.address.city ||
            !personCompany.address?.state_code,
      },

      {
        label: 'Documentos',
        actionButton: 'Salvar',
        icon: <DoneIcon fontSize="small" />,
        nextStep: closeModalHandler,
        render: isPerson ? (
          <UploadDocuments personId={createdPersonId} hasSuccessMessage />
        ) : (
          <UploadBusinessDocument
            personId={createdBusinessId}
            hasSuccessMessage
          />
        ),
      },
    ];

    return baseSteps;
  }, [
    formType,
    hasRole,
    person,
    personCompany,
    createdBusinessId,
    createdPersonId,
    hasBankInformation,
  ]);

  // Info: Valida se está no último Step (Documentos)
  const isLastStep = steps[activeStep]?.label === 'Documentos';

  useEffect(() => {
    setActiveStep(0);
  }, [formType]);

  return (
    <BaseSidesheet hideBackdrop open={isOpen} handleClose={closeModalHandler}>
      <SideSheetHeader>
        <StyledHeader>{title}</StyledHeader>
        <CloseIconWrapper onClick={closeModalHandler}>
          <CloseIcon color={theme.palette.brand.secondary.base as string} />
        </CloseIconWrapper>
      </SideSheetHeader>
      <Divider />
      <StyledContentWrapper>{steps[activeStep]?.render}</StyledContentWrapper>

      <StyledFooter>
        <StyledSteps>
          <Stepper activeStep={activeStep}>
            {steps.map(({ label }) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </StyledSteps>
        <Divider />
        <ModalFooter>
          <StyledButtonWrapper>
            <ButtonPrimary typeVariant="ghost" onClick={closeModalHandler}>
              {isLastStep ? 'Fechar' : 'Cancelar'}
            </ButtonPrimary>
          </StyledButtonWrapper>
          {activeStep > 0 && (
            <StyledButtonWrapper>
              <ButtonPrimary
                onClick={handleBack}
                typeVariant="outline"
                disabled={isLastStep}
              >
                <div style={{ marginRight: '10px' }}>
                  <ArrowBackIosNewIcon fontSize="small" />
                </div>
                Voltar
              </ButtonPrimary>
            </StyledButtonWrapper>
          )}
          <StyledButtonWrapper>
            <ButtonPrimary
              onClick={() => steps[activeStep].nextStep()}
              disabled={steps[activeStep].isButtonDisabled}
            >
              {steps[activeStep].actionButton}
              <div style={{ marginLeft: '10px' }}>{steps[activeStep].icon}</div>
            </ButtonPrimary>
          </StyledButtonWrapper>
        </ModalFooter>
      </StyledFooter>
      {isLoading && <Spinner fullscreen />}
    </BaseSidesheet>
  );
};

export default CreatePersonModal;
